import React, { useState } from 'react';
import axios from 'axios';
import { X, Loader2, Download, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';
import { API, formatErr } from './shared';

const downloadTemplate = async (format, authHeader) => {
  const res = await axios.get(`${API}/inventory/import/template?format=${format}`, {
    headers: authHeader(), responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `driveline-parts-template.${format}`;
  a.click();
  URL.revokeObjectURL(url);
};

const Result = ({ result }) => (
  <div className="space-y-2" data-testid="import-result">
    <div className="flex items-center gap-2 text-sm text-emerald-300">
      <CheckCircle2 size={16} />
      <span data-testid="import-result-summary">
        {result.created} added, {result.updated} updated, {result.errors.length} skipped
      </span>
    </div>
    {result.errors.length > 0 && (
      <ul className="max-h-40 overflow-y-auto rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 space-y-1" data-testid="import-errors">
        {result.errors.map((e) => (
          <li key={e.row}><span className="font-semibold">Row {e.row}:</span> {e.message}</li>
        ))}
      </ul>
    )}
  </div>
);

const ImportDialog = ({ onClose, onDone, authHeader }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await axios.post(`${API}/inventory/import`, body, { headers: authHeader() });
      setResult(data);
      onDone();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const getTemplate = (format) => downloadTemplate(format, authHeader)
    .catch((e) => setErr(formatErr(e.response?.data?.detail) || e.message));

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="import-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold">Import parts from a sheet</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="import-close"><X size={20} /></button>
        </div>
        <div className="px-6 py-5 space-y-4 text-sm text-slate-300">
          <p>
            Fill the template, one part per row. A row whose part number already exists updates that part
            (its stock is left alone, use Restock for that). Everything else is added as new.
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => getTemplate('xlsx')} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs hover:bg-white/10" data-testid="import-template-xlsx">
              <Download size={14} /> Excel template
            </button>
            <button type="button" onClick={() => getTemplate('csv')} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs hover:bg-white/10" data-testid="import-template-csv">
              <Download size={14} /> CSV template
            </button>
          </div>
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-white/20 px-4 py-4 hover:border-teal-400" data-testid="import-file-label">
            <Upload size={18} className="text-teal-300" />
            <span className="truncate">{file ? file.name : 'Choose a .xlsx or .csv file'}</span>
            <input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="import-file-input" />
          </label>
          {err && (
            <div className="flex items-start gap-2 text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="import-error">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {err}
            </div>
          )}
          {result && <Result result={result} />}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg" data-testid="import-cancel">Close</button>
            <button type="button" onClick={upload} disabled={!file || busy} className="px-4 py-2 text-sm bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-slate-950 font-semibold rounded-lg flex items-center gap-2" data-testid="import-submit">
              {busy && <Loader2 className="animate-spin" size={14} />} Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportDialog;
